import * as React from 'react';
import { Spinner } from '@cad-arc';
import { SubdivisionOption } from '@mark43/cad-api';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { FormSelect } from '../../forms/integrated-form-controls/form-select';
import { RequestAssistanceFormDisplayType } from '../types';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';

type EventClassPrefix = 'police' | 'fire' | 'ems';
const subdivisionNames = [
    'Subdivision5',
    'Subdivision4',
    'Subdivision3',
    'Subdivision2',
    'Subdivision1',
] as const;

type SubdivisionOptionsName = typeof subdivisionNames[number];
type SubdivisionFieldName = Lowercase<SubdivisionOptionsName>;
type SubdivisionOptionsPath = `${EventClassPrefix}${SubdivisionOptionsName}Options`;
type SubdivisionLabelPath = `${EventClassPrefix}${SubdivisionOptionsName}Label`;

function toLowerCase<T extends string>(value: T): Lowercase<T> {
    return value.toLowerCase() as Lowercase<T>;
}

export function RequestAssistanceSubdivisionFields({
    form,
    formIndex,
    eventClassPrefix,
    asDataListItem,
}: {
    form: UseFormReturn<RequestAssistanceFormDisplayType>;
    formIndex: number;
    eventClassPrefix: EventClassPrefix;
    asDataListItem?: boolean;
}): JSX.Element {
    const enabledFeatures = useEnabledFeatures();
    const bootstrapState = useBootstrapState();
    const allSubdivisionOptions =
        bootstrapState.subdivisionOptionsBootstrapResponse?.combinedSubdivisionOptionsBootstrapView;
    const agencySubdivisionOptions =
        bootstrapState.subdivisionOptionsBootstrapResponse
            ?.agencyIdToSubdivisionOptionsBootstrapView;
    const agencyValue = useWatch({
        control: form.control,
        name: `forms.${formIndex}.agencyProfileOption`,
    });

    const fieldParams:
        | {
              name: SubdivisionFieldName;
              options: SubdivisionOption[];
              label: string | undefined;
          }[]
        | undefined = React.useMemo(() => {
        if (!allSubdivisionOptions || !agencySubdivisionOptions) {
            return;
        }
        return subdivisionNames.map((optionsName) => {
            const fieldName = toLowerCase(optionsName);
            const fieldOptionsPath: SubdivisionOptionsPath = `${eventClassPrefix}${optionsName}Options`;
            const fieldLabelPath: SubdivisionLabelPath = `${eventClassPrefix}${optionsName}Label`;
            const agencyId = agencyValue?.value;
            const fieldOptions =
                enabledFeatures.multiAgencySubdivisions &&
                agencyId &&
                agencySubdivisionOptions[agencyId]
                    ? agencySubdivisionOptions[agencyId][fieldOptionsPath]
                    : allSubdivisionOptions[fieldOptionsPath];
            const fieldLabel =
                enabledFeatures.multiAgencySubdivisions &&
                agencyId &&
                agencySubdivisionOptions[agencyId]
                    ? agencySubdivisionOptions[agencyId][fieldLabelPath]
                    : allSubdivisionOptions[fieldLabelPath];

            return {
                name: fieldName,
                options: fieldOptions,
                label: fieldLabel,
            };
        });
    }, [
        eventClassPrefix,
        agencyValue,
        enabledFeatures,
        allSubdivisionOptions,
        agencySubdivisionOptions,
    ]);
    if (!fieldParams) {
        return <Spinner />;
    }

    return (
        <>
            {fieldParams.map((fieldParam, index) => {
                return (
                    <SubdivisionField
                        asDataListItem={asDataListItem}
                        name={fieldParam.name}
                        options={fieldParam.options}
                        label={fieldParam.label}
                        formIndex={formIndex}
                        subdivisionIndex={index}
                        form={form}
                        key={fieldParam.name}
                        eventClassPrefix={eventClassPrefix}
                    />
                );
            })}
        </>
    );
}

const SUBDIVISION_DEPTHS = [5, 4, 3, 2, 1] as const;

function SubdivisionField(props: {
    form: UseFormReturn<RequestAssistanceFormDisplayType>;
    name: SubdivisionFieldName;
    options: SubdivisionOption[];
    label: string | undefined;
    formIndex: number;
    subdivisionIndex: number;
    eventClassPrefix: EventClassPrefix;
    asDataListItem?: boolean;
}): JSX.Element | null {
    const enabledFeatures = useEnabledFeatures();
    const bootstrapState = useBootstrapState();
    const allSubdivisionOptions =
        bootstrapState.subdivisionOptionsBootstrapResponse?.combinedSubdivisionOptionsBootstrapView;
    const { name, formIndex, subdivisionIndex, options, label, form, eventClassPrefix } = props;
    const pathPrefix = `forms.${formIndex}` as const;
    React.useEffect(() => {
        const subscription = form.watch((data, { name: changedFieldName, type }) => {
            // TODO gate this on "non hierarchical subdivisions"
            const fullPath = `${pathPrefix}.${name}` as const;
            if (
                fullPath === changedFieldName &&
                type === 'change' &&
                !enabledFeatures.multiAgencySubdivisions &&
                allSubdivisionOptions
            ) {
                const depth = Math.max(...SUBDIVISION_DEPTHS) - subdivisionIndex;
                const subdivisionDepthsToActOn = SUBDIVISION_DEPTHS.reduce(
                    (acc, val) => {
                        if (val < depth) {
                            acc.prefill.push(val);
                        } else if (val > depth) {
                            acc.nullify.push(val);
                        }
                        return acc;
                    },
                    { nullify: [], prefill: [] } as {
                        nullify: number[];
                        prefill: number[];
                    }
                );
                // nullify fields that correspond to subdivisions depths more granular than the one being changed by the user
                for (const subdivisionDepthToNullify of subdivisionDepthsToActOn.nullify) {
                    const targetFieldPath = `${pathPrefix}.subdivision${
                        subdivisionDepthToNullify as 5 | 4 | 3 | 2
                    }` as const;
                    form.setValue(targetFieldPath, undefined);
                }
                // prefill fields that correspond to subdivisions depths that are less granular than the one being changed by the user
                let fieldValue = data.forms?.[formIndex]?.[name] as SubdivisionOption | undefined;
                for (const subdivisionDepthToPrefill of subdivisionDepthsToActOn.prefill) {
                    const { parentAttributeId } = fieldValue ?? {};
                    if (parentAttributeId) {
                        const subdivisionPathToPrefill = `Subdivision${
                            subdivisionDepthToPrefill as 4 | 3 | 2 | 1
                        }` as const;

                        const targetFieldPath = `${pathPrefix}.${toLowerCase(
                            subdivisionPathToPrefill
                        )}` as const;
                        const targetFieldOptionsPath: SubdivisionOptionsPath = `${eventClassPrefix}${subdivisionPathToPrefill}Options`;
                        const options = allSubdivisionOptions[targetFieldOptionsPath];
                        if (options) {
                            const optionToPrefill = options.find(
                                (option) => option.value === parentAttributeId
                            );
                            if (optionToPrefill) {
                                fieldValue = optionToPrefill;
                                form.setValue(targetFieldPath, optionToPrefill);
                            }
                        }
                    }
                }
            }
        });
        return () => {
            subscription.unsubscribe();
        };
    }, [
        formIndex,
        form,
        subdivisionIndex,
        pathPrefix,
        name,
        eventClassPrefix,
        options,
        enabledFeatures,
        allSubdivisionOptions,
    ]);
    if (!options.length) {
        return null;
    }
    return (
        <FormSelect
            length="sm"
            asEditable={false}
            asDataListItem={props.asDataListItem}
            key={name}
            control={props.form.control}
            label={label}
            name={`${pathPrefix}.${name}`}
            options={options}
            eventNotificationName="SUBDIVISION"
        />
    );
}
